/**
 * Pure name + type heuristics that bucket a row cell into one of the
 * Single-Row Details View categories (Identity / Profile / FKs / State /
 * Timestamps / Metrics / Metadata). FK/PK flags from MetaInfo always win
 * over heuristics — see `useCellCategorization`.
 */

import type { CategoryKey } from '../types/singleRowTypes';

const IDENTITY_NAME_PATTERNS = [
  /^id$/,
  /_id$/,
  /^slug$/,
  /^uuid$/,
  /_uuid$/,
  /^code$/,
  /_code$/,
  /^key$/,
  /_key$/,
  /^legacy_id$/,
];

const PROFILE_NAME_PATTERNS = [
  /^name$/,
  /_name$/,
  /^first_name$/,
  /^last_name$/,
  /^full_name$/,
  /^email$/,
  /_email$/,
  /^phone$/,
  /_phone$/,
  /^avatar$/,
  /_avatar$/,
  /^bio$/,
  /^description$/,
  /^title$/,
  /^label$/,
  /^display_name$/,
];

const STATE_NAME_PATTERNS = [
  /^status$/,
  /_status$/,
  /^state$/,
  /_state$/,
  /^stage$/,
  /^kind$/,
  /^type$/,
  /^role$/,
  /^level$/,
  /^priority$/,
  /^is_/,
  /^has_/,
  /^can_/,
  /^enabled$/,
  /^active$/,
  /^visible$/,
];

const TIMESTAMP_NAME_PATTERNS = [
  /_at$/,
  /_date$/,
  /_time$/,
  /^created$/,
  /^updated$/,
  /^modified$/,
  /^deleted$/,
  /^timestamp$/,
];

const METRIC_NAME_PATTERNS = [
  /^count$/,
  /_count$/,
  /^total$/,
  /_total$/,
  /^sum$/,
  /_sum$/,
  /^avg/,
  /_avg$/,
  /^max/,
  /_max$/,
  /^min/,
  /_min$/,
  /^score$/,
  /_score$/,
  /^rate$/,
  /_rate$/,
  /^amount$/,
  /_amount$/,
];

const TIMESTAMP_TYPE_PATTERNS = [
  /timestamp/i,
  /datetime/i,
  /^date$/i,
  /^time$/i,
];

const NUMERIC_TYPE_PATTERNS = [
  /^int/i,
  /^bigint/i,
  /^smallint/i,
  /^tinyint/i,
  /^decimal/i,
  /^numeric/i,
  /^float/i,
  /^double/i,
  /^real/i,
];

const BOOLEAN_TYPE_PATTERNS = [/bool/i, /^bit$/i];

const JSON_TYPE_PATTERNS = [/json/i];

/**
 * Inputs used to categorize one column. Kept minimal so the function stays
 * pure and easy to unit-test (no MetaInfo / store dependency).
 */
export type CategorizeColumnInput = {
  readonly columnName: string;
  readonly dataType: string;
  readonly isPrimaryKey?: boolean;
  readonly isForeignKey?: boolean;
  /**
   * Spatial flag computed by the CALLER (via `categorizeSpatialColumn`) so this
   * pure rules file never duplicates the per-engine spatial type lists (T11).
   * When true the column buckets into the `spatial` group.
   */
  readonly dataTypeIsSpatial?: boolean;
};

function nameMatches(name: string, patterns: ReadonlyArray<RegExp>): boolean {
  const normalized = name.toLowerCase();
  return patterns.some((p) => p.test(normalized));
}

function typeMatches(dataType: string, patterns: ReadonlyArray<RegExp>): boolean {
  if (!dataType) return false;
  return patterns.some((p) => p.test(dataType));
}

/**
 * Returns the category bucket for one column. Priority (high → low): FK,
 * PK, identity-name, timestamp, profile-name, state, metrics, json,
 * default. See test suite for the full naming-pattern matrix.
 */
export function categorizeColumn(input: CategorizeColumnInput): CategoryKey {
  const { columnName, dataType, isPrimaryKey, isForeignKey, dataTypeIsSpatial } = input;

  // Spatial wins over name/type heuristics (a geometry column is unambiguous);
  // FK/PK still take precedence since those are explicit schema facts.
  if (isForeignKey) return 'fk';
  if (dataTypeIsSpatial) return 'spatial';
  if (isPrimaryKey) return 'identity';

  if (nameMatches(columnName, IDENTITY_NAME_PATTERNS)) return 'identity';

  if (
    nameMatches(columnName, TIMESTAMP_NAME_PATTERNS) ||
    typeMatches(dataType, TIMESTAMP_TYPE_PATTERNS)
  ) {
    return 'timestamps';
  }

  if (nameMatches(columnName, PROFILE_NAME_PATTERNS)) return 'profile';

  if (
    nameMatches(columnName, STATE_NAME_PATTERNS) ||
    typeMatches(dataType, BOOLEAN_TYPE_PATTERNS)
  ) {
    return 'state';
  }

  if (
    nameMatches(columnName, METRIC_NAME_PATTERNS) &&
    typeMatches(dataType, NUMERIC_TYPE_PATTERNS)
  ) {
    return 'metrics';
  }

  if (typeMatches(dataType, JSON_TYPE_PATTERNS)) return 'metadata';

  return 'metadata';
}

/** Stable display order for the category groups in the row-details panel. */
export const CATEGORY_ORDER: ReadonlyArray<CategoryKey> = [
  'identity',
  'profile',
  'fk',
  'spatial',
  'state',
  'timestamps',
  'metrics',
  'metadata',
];

export const CATEGORY_LABELS: Readonly<Record<CategoryKey, string>> = {
  identity: 'Identity',
  profile: 'Profile',
  fk: 'Foreign keys',
  spatial: 'Spatial',
  state: 'State',
  timestamps: 'Timestamps',
  metrics: 'Counters & metrics',
  metadata: 'Metadata',
};
